import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MicIcon from "@mui/icons-material/Mic";
import "./MicGlow.css";
import { AudioPreview } from "../controls/AudioPreview";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../context/SnackbarContext";
import { StorageStatus } from "../display/StorageStatus";
import { useHomeFab } from "../../../context/HomeFabContext";

export default function FileSelectorDialog({
  open,
  onClose,
  onSubmit,
  fileType = "any",
  progress = 0,
  showRecordVoice = true // ✅ new prop to toggle mic button
}) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const { setDisableHomeFab } = useHomeFab();

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [stream, setStream] = useState(null);
  const [fileSizeOk, setFileSizeOk] = useState(false);
  const [uploading,setUploading] = useState(false);

  const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30 MB
  const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size < MAX_FILE_SIZE) {
                setFileSizeOk(true);
                console.log()
            }
            setSelectedFile(file);
            if (file.type.startsWith("image/")) {
                setPreviewUrl(URL.createObjectURL(file));
            } else {
                setPreviewUrl(null);
            }
        }
  };

    const startRecording = async () => {
        try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
            ? "audio/mp4"
            : "audio/webm";

            const mediaRecorder = new MediaRecorder(micStream, { mimeType });
            let chunks = [];

            mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
            mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: mimeType });
            const fileName = `recording-${Date.now()}.${mimeType === "audio/mp4" ? "mp4" : "webm"}`;
            const file = new File([blob], fileName, { type: mimeType });

            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(blob));
            };

            mediaRecorder.start();
            setRecorder(mediaRecorder);
            setStream(micStream);
            setIsRecording(true);
        } catch (err) {
            console.error("Recording failed:", err);
            alert("Audio recording is not supported on this browser.");
        }
    };


  const stopRecording = () => {
    if (recorder) recorder.stop();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsRecording(false);
    setFileSizeOk(true);
  };

  const handleSubmit = () => {
    if (selectedFile) {
      onSubmit(selectedFile);
      setUploading(true);
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    if (uploading && (progress > 0))
    {
      showSnackbar(t('files.messages.background_uploading'), "success");
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setFileSizeOk(false);
    setUploading(false);
    setDisableHomeFab(false);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setFileSizeOk(false);
      setUploading(false);
      setDisableHomeFab(true);
      setMessage(t('files.messages.not_saved_until_uploaded'));
      setFileUploaded(false);
    } else {
      setDisableHomeFab(false);
    }
  }, [open]);

  const [message, setMessage] = useState(t('files.messages.not_saved_until_uploaded'));
  const [fileUploaded, setFileUploaded] = useState(false);

  useEffect(() => {
    if (progress === 100) {
      setMessage(t('files.messages.processing'));
      setFileUploaded(true);
    } 
    if (progress === 0 && fileUploaded)
    {
      setMessage(t('files.messages.upload_success'));
    }
  }, [progress]);

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      fullWidth 
      maxWidth="sm" 
      disableRestoreFocus
    >
      <DialogTitle>File Upload</DialogTitle>
      <DialogContent>
        {selectedFile && (
          <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
            {previewUrl && selectedFile.type.startsWith("image/") ? (
              <img src={previewUrl} alt={selectedFile.name} style={{ maxWidth: "100%", maxHeight: 200 }} />
            ) : previewUrl && selectedFile.type.startsWith("audio/") ? (
              <AudioPreview src={previewUrl} />
            ) : (
              <>
                <InsertDriveFileIcon sx={{ fontSize: 48 }} color="action" />
                <Typography variant="body2">
                  {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                </Typography>
              </>
            )}

            {progress > 0 && (
              <Box width="100%" mt={2}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" color="textSecondary">
                  Uploading... {progress}%
                </Typography>
              </Box>
            )}
            {fileSizeOk ?
            <Typography variant="body2" color="warning">
                {message}
            </Typography>
            :
            <Typography variant="body2" color="error">
                {t("files.messages.file_too_large")}
            </Typography>
            }
          </Box>
        )}

        {!uploading ?
        <>
        {/* Take Picture */}
        <Box mt={2}>
          <input type="file" id="camera-input" style={{ display: "none" }} accept="image/*" capture="environment" onChange={handleFileChange} />
          <label htmlFor="camera-input">
            <Button fullWidth variant="outlined" component="span" startIcon={<PhotoCameraIcon />}>
              Take Picture
            </Button>
          </label>
        </Box>

        {/* Select File */}
        <Box mt={2}>
          <input type="file" id="file-input" style={{ display: "none" }} accept="*/*" onChange={handleFileChange} />
          <label htmlFor="file-input">
            <Button fullWidth variant="outlined" component="span" startIcon={<UploadFileIcon />}>
              Select File
            </Button>
          </label>
        </Box>

        {/* 🎤 Record Voice (optional) */}
        {showRecordVoice && (
          <Box mt={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={isRecording ? stopRecording : startRecording}
              className={isRecording ? "glow-button" : ""}
              startIcon={<MicIcon />}
            >
              {isRecording ? "Stop Recording" : "Record Voice"}
            </Button>
          </Box>
        )}
        <Box mt={2}>
          <StorageStatus />
        </Box>
        </>
        : null }
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSubmit} disabled={uploading || isRecording}>
          Upload
        </Button>
        <Button onClick={handleClose} disabled={isRecording}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}